import { type ReactNode, useMemo, useState } from 'react'

import { Button } from '@/components/ui'
import { useChatMessages, useChatSending } from '@/modules/chat/hooks'
import type { ChatMessage } from '@/modules/chat/types'
import { cn } from '@/utils'
import {
  type ApplyToolTarget,
  type ConfirmationState,
  findConfirmationOutcome,
  resolveConfirmationState,
} from './confirmationOutcome'
import {
  expiresWithinAnHour,
  formatExpiresAt,
  useExpiryCountdown,
} from './useExpiryCountdown'

// Shared state machine + footer for the staged-edit confirmation cards
// (bulk edit, removal, report edit). Each card stages a token-confirmed batch
// the user applies or cancels; the card's state comes from the apply tool's
// result in the session, not from the click alone.

interface ConfirmationCardStateOptions extends ApplyToolTarget {
  expiresAt: string
  // Set when the cancel is kept outside the card, e.g. saved on the server.
  cancelled?: boolean
  // Set when the server says the edit was applied but this chat has no result.
  applied?: boolean
}

export function useConfirmationCardState({
  expiresAt,
  applyToolName,
  tokenField,
  token,
  cancelled = false,
  applied = false,
}: ConfirmationCardStateOptions) {
  const messages = useChatMessages()
  const sendingMessage = useChatSending()
  const expired = useExpiryCountdown(expiresAt)
  const [messagesAtRequest, setMessagesAtRequest] = useState<
    ChatMessage[] | null
  >(null)
  const [cancelledLocally, setCancelledLocally] = useState(false)
  const outcome = useMemo(
    () =>
      findConfirmationOutcome(messages, { applyToolName, tokenField, token }),
    [messages, applyToolName, tokenField, token],
  )

  // The turn-end refetch swaps in new messages. If that turn made no apply
  // call for this token, stop showing Applying so the user can try again.
  const requested =
    messagesAtRequest !== null &&
    (sendingMessage || messagesAtRequest === messages)
  const setRequested = (isRequested: boolean) =>
    setMessagesAtRequest(isRequested ? messages : null)

  const state = resolveConfirmationState(outcome, {
    applied,
    cancelled: cancelled || cancelledLocally,
    requested,
    expired,
  })
  const disabled = state.status !== 'pending' || sendingMessage

  return { state, disabled, setRequested, setCancelledLocally }
}

interface ConfirmationFooterProps {
  expiresAt: string
  state: ConfirmationState
  disabled: boolean
  appliedMessage: string
  expiredMessage: string
  confirmLabel?: ReactNode
  extraAction?: ReactNode
  onApply: () => void
  onCancel: () => void
}

export function ConfirmationFooter({
  expiresAt,
  state,
  disabled,
  appliedMessage,
  expiredMessage,
  confirmLabel = 'Confirm',
  extraAction,
  onApply,
  onCancel,
}: ConfirmationFooterProps) {
  const failed = state.status === 'expired' || state.status === 'not_applied'
  return (
    <footer className="flex shrink-0 items-center justify-between gap-3 border-t px-4 py-2.5">
      <span
        className={cn(
          'text-xs',
          failed ? 'text-destructive' : 'text-muted-foreground',
        )}
      >
        {stateMessage(state, expiresAt, appliedMessage, expiredMessage)}
      </span>
      <span className="flex shrink-0 gap-1.5">
        <Button
          type="button"
          theme="ghost"
          size="xs"
          disabled={disabled}
          onClick={onCancel}
        >
          Cancel
        </Button>
        {extraAction}
        <Button type="button" size="xs" disabled={disabled} onClick={onApply}>
          {confirmLabel}
        </Button>
      </span>
    </footer>
  )
}

export function stateMessage(
  state: ConfirmationState,
  expiresAt: string,
  appliedMessage: string,
  expiredMessage: string,
): string {
  switch (state.status) {
    case 'applied':
      return appliedMessage
    case 'applying':
      return 'Applying...'
    case 'cancelled':
      return 'Cancelled.'
    case 'expired':
      return expiredMessage
    case 'not_applied':
      return state.reason ? `Not applied: ${state.reason}.` : 'Not applied.'
    case 'pending':
      return expiresWithinAnHour(expiresAt)
        ? `Expires ${formatExpiresAt(expiresAt)}.`
        : ''
  }
}
